import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { HTTPError } from "ky";
import { api } from "@/api/client";
import { dashboardQueryKeyForSlug, dashboardRequestOptions } from "@/api/hooks/dashboard-request";
import {
  isUnavailableOnboardingSelection,
  onboardingStatusOptions,
} from "@/api/hooks/use-onboarding-status";
import type { DashboardOverview, OnboardingStatus, User } from "@/api/types";
import {
  canonicalDashboardPath,
  currentDashboardSlug,
  dashboardSectionPath,
} from "@/lib/dashboard-context";
import { isErrorEnvelope } from "@/lib/handle-form-error";
import { clearBusinessId, storedBusinessId } from "@/lib/onboarding-storage";
import { assertCurrentSession, sessionRevision } from "@/lib/session-request-scope";
import {
  enterDashboardContext,
  renameDashboardContext,
  transitionSession,
} from "@/lib/session-transition";
import { useAuthUIStore } from "@/stores/auth-ui";

function envelopeHasCode(error: HTTPError, code: string): boolean {
  if (!isErrorEnvelope(error.data)) return false;
  return error.data.errors.some((entry) => entry.code === code);
}

interface RouteContext {
  queryClient: QueryClient;
}

interface RouteLocation {
  pathname: string;
  searchStr?: string;
}

interface GuardArgs {
  context: RouteContext;
  location?: RouteLocation;
}

function isAuthPath(path: string): boolean {
  return path === "/auth" || path.startsWith("/auth/");
}

function postAuthDestination(path: string | null): string {
  if (!path || isAuthPath(path)) return "/";
  return path;
}

export function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

async function fetchCurrentUserOrNull(): Promise<User | null> {
  try {
    return await api.get("me").json<User>();
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 401) {
      return null;
    }
    throw error;
  }
}

async function ensureCurrentUser(context: RouteContext): Promise<User | null> {
  return context.queryClient.ensureQueryData<User | null>({
    queryKey: ["me"],
    queryFn: fetchCurrentUserOrNull,
    staleTime: 5 * 60_000,
  });
}

async function fetchOnboardingStatus(context: RouteContext): Promise<OnboardingStatus> {
  const businessId = storedBusinessId();
  try {
    return await context.queryClient.fetchQuery({
      ...onboardingStatusOptions(businessId),
      staleTime: 0,
    });
  } catch (error) {
    if (
      businessId &&
      storedBusinessId() === businessId &&
      isUnavailableOnboardingSelection(error)
    ) {
      clearBusinessId();
      context.queryClient.removeQueries({
        queryKey: ["onboarding-status", businessId],
        exact: true,
      });
      context.queryClient.removeQueries({ queryKey: ["onboarding-status"], exact: true });
      throw redirect({ to: "/onboarding/select-business", replace: true });
    }
    throw error;
  }
}

/** Maps `GET /onboarding/status` to the SPA route the owner should resume. */
export function resumeOnboardingPath(status: OnboardingStatus): string {
  if (!status.user.phone_verified) {
    return "/onboarding/verify-phone";
  }

  const business = status.businesses.find((entry) => entry.id === status.selected_business_id);
  if (!business) {
    return "/onboarding/business";
  }

  if (business.onboarding_state === "completed") {
    return dashboardSectionPath(business.slug, "calendar");
  }

  if (business.missing.includes("service_categories")) {
    return "/onboarding/services";
  }
  if (business.missing.includes("team_size")) {
    return "/onboarding/team-size";
  }
  if (business.missing.length === 0) {
    return "/onboarding/complete";
  }

  return "/onboarding/location";
}

export async function requireAuth({ context }: GuardArgs) {
  const user = await ensureCurrentUser(context);

  if (!user) {
    const currentPath = window.location.pathname;
    if (currentPath !== "/auth/sign-in") {
      useAuthUIStore.getState().setRedirect(currentReturnPath());
    }
    throw redirect({ to: "/auth/sign-in" });
  }
}

export async function requireAnonymous({ context }: GuardArgs) {
  const user = await ensureCurrentUser(context);
  if (!user) return;

  const { redirectAfterLogin, setRedirect } = useAuthUIStore.getState();
  const destination = postAuthDestination(redirectAfterLogin);
  setRedirect(null);
  throw redirect({ to: destination });
}

export async function requireOnboardingSignUpAnonymous({ context }: GuardArgs) {
  const user = await ensureCurrentUser(context);
  if (!user) return;

  throw redirect({ to: "/onboarding/start" });
}

export async function requireProOnboardingStart({ context }: GuardArgs) {
  const user = await ensureCurrentUser(context);

  if (!user) {
    useAuthUIStore.getState().setRedirect("/onboarding/start");
    throw redirect({ to: "/auth/sign-in" });
  }

  const status = await fetchOnboardingStatus(context);
  throw redirect({ to: resumeOnboardingPath(status) });
}

export async function requireBusinessDashboard({ context, location }: GuardArgs) {
  await requireAuth({ context });
  const pathname = location?.pathname ?? window.location.pathname;
  const dashboardSlug = currentDashboardSlug(pathname);
  enterDashboardContext(context.queryClient, dashboardSlug ?? "selected");
  const revision = sessionRevision();

  try {
    const overview = await context.queryClient.ensureQueryData<DashboardOverview>({
      queryKey: dashboardQueryKeyForSlug(dashboardSlug, "overview"),
      queryFn: () =>
        api.get("dashboard/overview", dashboardRequestOptions(pathname)).json<DashboardOverview>(),
      staleTime: 60_000,
    });
    assertCurrentSession(revision);
    if (dashboardSlug !== overview.business.slug) {
      const slug = overview.business.slug;
      renameDashboardContext(context.queryClient, {
        previousSlug: dashboardSlug ?? "selected",
        slug,
        businessId: overview.business.id,
        revision,
      });
      context.queryClient.setQueryData(dashboardQueryKeyForSlug(slug, "overview"), overview);
      const target = canonicalDashboardPath(pathname, slug);
      throw redirect({
        to: `${target}${location?.searchStr ?? window.location.search}`,
        replace: true,
      });
    }
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 403) {
      if (envelopeHasCode(error, "no_business_access")) {
        transitionSession(
          context.queryClient,
          context.queryClient.getQueryData<User | null>(["me"]),
        );
        const status = await fetchOnboardingStatus(context);

        if (status.businesses.length > 0) {
          throw redirect({ to: resumeOnboardingPath(status) });
        }

        throw redirect({ to: "/onboarding/start" });
      }
    }
    throw error;
  }
}

export async function requireDashboardOwner({ context, location }: GuardArgs) {
  await requireBusinessDashboard({ context, location });
  const pathname = location?.pathname ?? window.location.pathname;
  const dashboardSlug = currentDashboardSlug(pathname);

  const overview = context.queryClient.getQueryData<DashboardOverview>(
    dashboardQueryKeyForSlug(dashboardSlug, "overview"),
  );
  if (overview?.role !== "owner") {
    const slug = overview?.business.slug ?? dashboardSlug;
    throw redirect({ to: slug ? dashboardSectionPath(slug, "calendar") : "/dashboard/calendar" });
  }
}
